import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaPencil, FaTrashCan } from "react-icons/fa6";

export const DEFAULT_DECK_IMAGE =
  "https://zubrusstorage2.blob.core.windows.net/avatar-colour/DeckDefault.png";

const STATUS_BADGE_CLASS = {
  PUBLISHED: "bg-green-500 text-white",
  PRIVATE: "bg-blue-600 text-white",
  HIDDEN: "bg-gray-600 text-white",
  WORK_IN_PROGRESS: "bg-amber-400 text-gray-900",
  DELETED: "bg-red-500 text-white",
};

export function deckStatusBadgeClass(status) {
  const key = String(status || "").toUpperCase();
  return STATUS_BADGE_CLASS[key] || "bg-gray-500 text-white";
}

const formatGrades = (schoolGrades) => {
  if (!schoolGrades?.length) return "N/A";
  const grades = schoolGrades.filter(
    (grade) => grade && String(grade).trim() !== ""
  );
  return grades.length > 0 ? grades.join(", ") : "N/A";
};

/**
 * Shared deck card for Workshop, Quiz, and other deck grids.
 *
 * Optional creator actions: pass `onEdit` / `onDelete` to show buttons.
 * Optional overlays: `showStatus`, `showLanguage`.
 */
export default function DeckCard({
  deck,
  to,
  showStatus = false,
  showLanguage = false,
  titleLines = 2,
  onEdit,
  onDelete,
  className = "",
}) {
  const { t } = useTranslation();
  const deckId = deck?.deckId || deck?._id;
  const href = to || (deckId ? `/workshop/${deckId}` : "#");
  const imageUrl =
    deck?.largeImageUrl || deck?.smallImageUrl || DEFAULT_DECK_IMAGE;
  const showActions = Boolean(onEdit || onDelete);

  const meta = [
    deck?.mainSubject,
    `${t("gradeLabel")} ${formatGrades(deck?.schoolGrades)}`,
    showLanguage && deck?.language
      ? String(deck.language).toUpperCase()
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`group relative flex flex-col rounded-2xl border border-gray-100 bg-white p-3.5 transition-shadow duration-200 hover:shadow-md ${className}`}
    >
      {showActions ? (
        <div className="absolute right-5 top-5 z-20 flex items-center gap-1.5">
          {onEdit ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(deck);
              }}
              className="rounded-2xl border border-gray-100 bg-white p-2 text-blue-600 transition-colors hover:bg-blue-50"
              aria-label="Edit deck"
              title={t("edit") || "Edit"}
            >
              <FaPencil className="size-3.5" />
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(deck);
              }}
              className="rounded-2xl border border-gray-100 bg-white p-2 text-red-600 transition-colors hover:bg-red-50"
              aria-label="Delete deck"
              title={t("Delete") || "Delete"}
            >
              <FaTrashCan className="size-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}

      <Link to={href} className="flex min-w-0 flex-1 flex-col">
        <div className="relative mb-3.5 aspect-[4/3] overflow-hidden rounded-2xl bg-[#FFF5E6]">
          <img
            src={imageUrl}
            alt={deck?.deckName || "Deck"}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
          />

          {showStatus && deck?.status ? (
            <span
              className={`absolute left-0 top-0 rounded-br-2xl px-3 py-1.5 text-xs font-semibold ${deckStatusBadgeClass(deck.status)}`}
            >
              {t(deck.status)}
            </span>
          ) : null}
        </div>

        <div className="min-w-0 px-0.5">
          <h3
            className={`text-lg font-bold leading-snug text-black transition-colors group-hover:text-darkblue ${
              titleLines === 1 ? "line-clamp-1" : "line-clamp-2"
            }`}
          >
            {deck?.deckName}
          </h3>

          {meta ? (
            <p className="mt-1 truncate text-[13px] text-lightgray">{meta}</p>
          ) : null}
        </div>
      </Link>
    </div>
  );
}

export function DeckCardSkeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-2xl border border-gray-100 bg-white p-3.5 ${className}`}
    >
      <div className="mb-3.5 aspect-[4/3] rounded-2xl bg-gray-100" />
      <div className="space-y-2 px-0.5">
        <div className="h-3 w-2/3 rounded bg-gray-100" />
        <div className="h-5 w-full rounded bg-gray-100" />
      </div>
    </div>
  );
}
